import * as React from 'react';
import { styled } from '@linaria/react';
import { RotateCcw } from 'lucide-react';

import { clamp } from '@/utils';
import useGrabSfx from '@/hooks/use-grab-sfx';
import useTickSfx from '@/hooks/use-tick-sfx';

import ColorModeContainer from '@/components/ColorModeContainer';
import SecondaryButton from '@/components/ButtonPlayful/ButtonSecondary';
import SvgWithGrid from '@/components/SvgWithGrid';

import PathsPseudoCodeSnippet from './PathsPseudoCodeSnippet';

interface Point {
  x: number;
  y: number;
}

type QuadraticBezierPoints = [Point, Point, Point];
type CubicBezierPoints = [Point, Point, Point, Point];

interface Props {
  variant: 'quadratic' | 'cubic';
  initialPointsOverride?: Array<Point>;
}

const VIEWBOX_SIZE = 16;
const RENDERED_SIZE = 320;
const MULTIPLIER = RENDERED_SIZE / VIEWBOX_SIZE;
const ALLOW_EXTENDED_BY = 1;
const INITIAL_QUADRATIC_POINTS: QuadraticBezierPoints = [
  { x: 2, y: 2 },
  { x: 2, y: 14 },
  { x: 14, y: 14 },
];
const INITIAL_CUBIC_POINTS: CubicBezierPoints = [
  { x: 2, y: 2 },
  { x: 2, y: 14 },
  { x: 14, y: 2 },
  { x: 14, y: 14 },
];

function BezierPathDemo({ variant, initialPointsOverride }: Props) {
  const id = React.useId().replace(/:/g, '');
  const controlPointMaskId = `${id}-control-point-mask`;

  const [playStartGrab, playReleaseGrab] = useGrabSfx();
  const playTick = useTickSfx();

  const initialPoints =
    initialPointsOverride ??
    (variant === 'quadratic'
      ? INITIAL_QUADRATIC_POINTS
      : INITIAL_CUBIC_POINTS);

  const svgRef = React.useRef<SVGSVGElement>(null);

  const [points, setPoints] =
    React.useState<Array<Point>>(initialPoints);
  const pointsRef = React.useRef<Array<Point>>(points);
  pointsRef.current = points;

  const [activePointIndex, setActivePointIndex] = React.useState<
    number | null
  >(null);

  // The fallback input mechanism is to allow keyboard users to focus the control points and move them with arrow keys.
  // The trouble is that focusing the element also sets `activePointIndex`, which means that if the user keyboard-focuses the input and THEN moves the mouse, the point would jump to the cursor.
  // To prevent this, we track which input mechanism is currently active:
  // • on focus, we set it to "keyboard"
  // • on pointer down, we set it to "pointer"
  // • on blur and on pointer up, we set it to `null`
  const inputMechanismRef = React.useRef<
    'keyboard' | 'pointer' | null
  >(null);

  React.useEffect(() => {
    if (
      activePointIndex === null ||
      inputMechanismRef.current === 'keyboard'
    ) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const svg = svgRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      let x = (event.clientX - rect.left) / MULTIPLIER;
      let y = (event.clientY - rect.top) / MULTIPLIER;

      x = Math.round(
        clamp(x, -ALLOW_EXTENDED_BY, VIEWBOX_SIZE + ALLOW_EXTENDED_BY)
      );
      y = Math.round(
        clamp(y, -ALLOW_EXTENDED_BY, VIEWBOX_SIZE + ALLOW_EXTENDED_BY)
      );

      const currentPosition = pointsRef.current[activePointIndex];
      const isChangingPosition =
        x !== currentPosition.x || y !== currentPosition.y;

      if (!isChangingPosition) {
        return;
      }

      playTick();

      setPoints(
        (currentValue: Array<Point>): Array<Point> =>
          // TS doesn't understand that this .map call will always return three objects, to satisfy [Point, Point, Point]
          currentValue.map((point, index) =>
            index === activePointIndex ? { x, y } : point
          )
      );
    };

    const handlePointerUp = () => {
      setActivePointIndex(null);
      document.body.style.cursor = 'default';
      inputMechanismRef.current = null;
      playReleaseGrab();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [activePointIndex, playReleaseGrab, playTick]);

  const handleKeyPress = (key: string, index: number) => {
    let nudgeX = 0;
    let nudgeY = 0;

    if (key === 'ArrowLeft') {
      nudgeX = -1;
    } else if (key === 'ArrowRight') {
      nudgeX = 1;
    } else if (key === 'ArrowUp') {
      nudgeY = -1;
    } else if (key === 'ArrowDown') {
      nudgeY = 1;
    }

    setPoints(
      (currentValue: Array<Point>): Array<Point> =>
        currentValue.map((point, i) => {
          if (i !== index) {
            return point;
          }

          const { x, y } = point;

          let newX = x + nudgeX;
          let newY = y + nudgeY;

          newX = Math.round(
            clamp(
              newX,
              -ALLOW_EXTENDED_BY,
              VIEWBOX_SIZE + ALLOW_EXTENDED_BY
            )
          );
          newY = Math.round(
            clamp(
              newY,
              -ALLOW_EXTENDED_BY,
              VIEWBOX_SIZE + ALLOW_EXTENDED_BY
            )
          );

          return { x: newX, y: newY };
        })
    );
  };

  const handleStartDragging = (index: number) => {
    inputMechanismRef.current = 'pointer';
    setActivePointIndex(index);
    document.body.style.cursor = 'grabbing';
    playStartGrab();
  };

  const handleReset = () => {
    setPoints(initialPoints);
  };

  const indices = variant === 'quadratic' ? [0, 1, 2] : [0, 1, 2, 3];

  return (
    <OuterWrapper>
      <Wrapper colorMode="dark">
        <CodeWrapper>
          <PathsPseudoCodeSnippet
            variant={variant}
            points={points}
            activePointIndex={activePointIndex}
          />
        </CodeWrapper>
        <ResetButton Icon={RotateCcw} onClick={handleReset}>
          Reset
        </ResetButton>
        <SvgWrapper>
          <Svg
            ref={svgRef}
            gridGap={1}
            lineWidth={0.1}
            viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
            style={{ width: RENDERED_SIZE, height: RENDERED_SIZE }}
          >
            <defs>
              <mask
                id={controlPointMaskId}
                maskUnits="userSpaceOnUse"
              >
                <rect width="100%" height="100%" fill="white" />
                <circle
                  cx={points[0].x}
                  cy={points[0].y}
                  r={0.75}
                  fill="black"
                />
                <circle
                  cx={points[1].x}
                  cy={points[1].y}
                  r={0.75}
                  fill="black"
                />
                <circle
                  cx={points[2].x}
                  cy={points[2].y}
                  r={0.75}
                  fill="black"
                />
                {points[3] && (
                  <circle
                    cx={points[3].x}
                    cy={points[3].y}
                    r={0.75}
                    fill="black"
                  />
                )}
              </mask>
            </defs>
            <HandleLine
              mask={`url(#${controlPointMaskId})`}
              x1={points[0].x}
              y1={points[0].y}
              x2={points[1].x}
              y2={points[1].y}
            />
            <HandleLine
              mask={`url(#${controlPointMaskId})`}
              x1={points[1].x}
              y1={points[1].y}
              x2={points[2].x}
              y2={points[2].y}
            />
            {variant === 'cubic' && (
              <HandleLine
                mask={`url(#${controlPointMaskId})`}
                x1={points[2].x}
                y1={points[2].y}
                x2={points[3].x}
                y2={points[3].y}
              />
            )}
            {variant === 'quadratic' ? (
              <Curve
                d={`
                  M ${points[0].x} ${points[0].y}
                  Q ${points[1].x} ${points[1].y} ${points[2].x} ${points[2].y}
                `}
              />
            ) : (
              <Curve
                d={`
                  M ${points[0].x} ${points[0].y}
                  C ${points[1].x} ${points[1].y} ${points[2].x} ${points[2].y} ${points[3].x} ${points[3].y}
                `}
              />
            )}
          </Svg>
          {indices.map((index) => {
            const isEndPoint =
              index === 0 || index === indices.length - 1;
            const Element = isEndPoint ? EndPoint : ControlPoint;

            return (
              <Element
                key={index}
                onPointerDown={() => {
                  handleStartDragging(index);
                }}
                onFocus={() => {
                  if (inputMechanismRef.current === 'pointer') {
                    return;
                  }

                  setActivePointIndex(index);
                  inputMechanismRef.current = 'keyboard';
                }}
                onBlur={() => {
                  if (inputMechanismRef.current === 'keyboard') {
                    setActivePointIndex(null);
                    inputMechanismRef.current = null;
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Delete') {
                    handleReset();
                  } else if (event.key.includes('Arrow')) {
                    event.preventDefault();
                    handleKeyPress(event.key, index);
                  }
                }}
                style={{
                  top: points[index].y * MULTIPLIER,
                  left: points[index].x * MULTIPLIER,
                }}
              >
                <PointLabel draggable={false}>
                  ({points[index].x}, {points[index].y})
                </PointLabel>
              </Element>
            );
          })}
        </SvgWrapper>
      </Wrapper>
    </OuterWrapper>
  );
}

const OuterWrapper = styled.div`
  container-type: inline-size;
`;

const Wrapper = styled(ColorModeContainer)`
  display: grid;
  grid-template-areas:
    'code svg'
    'reset svg';
  grid-template-columns: 1fr ${RENDERED_SIZE}px;
  grid-template-rows: 1fr auto;
  gap: 16px;
  padding: 16px;
  margin-inline: -16px;
  border: var(--page-border);
  border-radius: 8px;
  margin-bottom: 64px;

  @container (max-width: 37.5rem) {
    grid-template-areas:
      'code'
      'svg'
      'reset';
    align-content: center;
    grid-template-columns: 1fr;
  }
`;

const CodeWrapper = styled.div`
  grid-area: code;
  outline: 1px solid var(--color-gray-300);
  border-radius: 4px;
`;

const ResetButton = styled(SecondaryButton)`
  grid-area: reset;
`;

const SvgWrapper = styled.div`
  grid-area: svg;
  justify-self: center;
  align-self: start;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Svg = styled(SvgWithGrid)`
  display: block;
  outline: 1px solid var(--color-gray-300);
  border-radius: 4px;
`;

const HandleLine = styled.line`
  stroke: white;
  stroke-opacity: 0.5;
  stroke-width: 0.125px;
  stroke-dasharray: 0.5 0.5;
  stroke-linecap: round;
`;

const Curve = styled.path`
  fill: none;
  stroke: var(--wham-primary-500);
  stroke-width: 0.5px;
`;

const EndPoint = styled.button`
  position: absolute;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--color-text);
  transform: translate(-50%, -50%);
  border: 4px solid var(--color-background);
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

const ControlPoint = styled(EndPoint)`
  background-color: transparent;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 8px solid var(--color-background);
    border-radius: 50%;
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 4px solid var(--color-text);
    border-radius: 50%;
  }
`;

const PointLabel = styled.div`
  position: absolute;
  top: -8px;
  left: 50%;
  white-space: nowrap;
  transform: translate(-50%, -100%);
  background: var(--color-background);
  user-select: none;
  font-size: 0.75rem;
  font-family: var(--font-family-mono);
  font-weight: 500;
  color: white;
  padding: 2px 4px;
  border-radius: 4px;
  pointer-events: none;
  opacity: 0;
  transition:
    opacity 500ms,
    color 200ms;

  ${EndPoint}:active &,
  ${EndPoint}:focus-visible & {
    color: var(--wham-primary-500);
    transition:
      opacity 100ms,
      color 100ms;
    opacity: 1;
  }
`;

export default BezierPathDemo;
